/* eslint-disable react/display-name */
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { flatten } from "@turf/turf";
import type { Feature, FeatureCollection, Geometry } from "geojson";
import { forwardRef, useImperativeHandle, useRef } from "react";
import type { ControlPosition, MapRef } from "react-map-gl";
import { useControl } from "react-map-gl";
import("@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css");
type DrawControlProps = ConstructorParameters<typeof MapboxDraw>[0] & {
  position?: ControlPosition;
  onCreate?: (evt: MapboxDraw.DrawCreateEvent) => void;
  onUpdate?: (evt: MapboxDraw.DrawUpdateEvent) => void;
  onDelete?: (evt: MapboxDraw.DrawDeleteEvent) => void;
  initialGeometry?: Feature | FeatureCollection | Geometry | null;
};

export const DrawControl = forwardRef((props: DrawControlProps, ref) => {
  const { onCreate, onUpdate, onDelete, position, initialGeometry } = props;

  const drawControl = useRef(new MapboxDraw(props));

  useControl<MapboxDraw>(
    () => drawControl.current,
    ({ map }) => {
      if (initialGeometry != null) {
        drawControl.current.add(flatten(initialGeometry));
      }

      if (onCreate != null) {
        map.on("draw.create", onCreate);
      }

      if (onUpdate != null) {
        map.on("draw.update", onUpdate);
      }

      if (onDelete != null) {
        map.on("draw.delete", onDelete);
      }
    },
    ({ map }: { map: MapRef }) => {
      if (onCreate != null) {
        map.off("draw.create", onCreate);
      }

      if (onUpdate != null) {
        map.off("draw.update", onUpdate);
      }

      if (onDelete != null) {
        map.off("draw.delete", onDelete);
      }
    },
    {
      position,
    },
  );

  useImperativeHandle(ref, () => drawControl.current, [drawControl]); // This way I exposed drawControl outside the component

  return null;
});
